import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardMedia,
    Slide,
    Chip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CloseIcon from '@mui/icons-material/Close';
import { forwardRef } from 'react';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StickMediaPlayer = ({ links }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [openModal, setOpenModal] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);

    const handleOpenModal = (media) => {
        setSelectedMedia(media);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedMedia(null);
    };

    const getMediaIcon = (type) => {
        switch (type.toLowerCase()) {
            case 'audio':
                return <AudioFileIcon />;
            case 'video':
                return <VideoFileIcon />;
            case 'image':
                return <ImageIcon />;
            default:
                return <AudioFileIcon />;
        }
    };

    const renderMediaPreview = (media) => {
        const { url, type, title, description } = media;

        switch (type.toLowerCase()) {
            case 'audio':
                return (
                    <Card
                        sx={{
                            mb: 1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: theme.shadows[4],
                                transform: 'translateY(-2px)'
                            }
                        }}
                        onClick={() => handleOpenModal(media)}
                    >
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AudioFileIcon color="primary" />
                                <Chip label="Audio" size="small" color="primary" variant="outlined" />
                            </Box>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {description}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                );

            case 'video':
                return (
                    <Card
                        sx={{
                            mb: 1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: theme.shadows[4],
                                transform: 'translateY(-2px)'
                            }
                        }}
                        onClick={() => handleOpenModal(media)}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <video
                                style={{
                                    width: '100%',
                                    height: isMobile ? '100px' : '150px',
                                    objectFit: 'cover',
                                    borderRadius: '4px 4px 0 0',
                                    backgroundColor: '#f5f5f5'
                                }}
                                preload="metadata"
                                muted
                                playsInline
                                onLoadedMetadata={(e) => {
                                    // Seek to 1 second to get a better thumbnail
                                    e.target.currentTime = 1;
                                }}
                            >
                                <source src={url} type="video/mp4" />
                                <source src={url} type="video/webm" />
                                <source src={url} type="video/ogg" />
                                {/* Fallback for unsupported video */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                                        backgroundSize: '20px 20px',
                                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                                    }}
                                >
                                    <VideoFileIcon sx={{ fontSize: '3rem', color: theme.palette.grey[400] }} />
                                </Box>
                            </video>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    borderRadius: '50%',
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <PlayArrowIcon sx={{ color: 'white', fontSize: '2rem' }} />
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <VideoFileIcon color="primary" />
                                <Chip label="Video" size="small" color="primary" variant="outlined" />
                            </Box>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {description}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                );

            case 'image':
                return (
                    <Card
                        sx={{
                            mb: 1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: theme.shadows[4],
                                transform: 'translateY(-2px)'
                            }
                        }}
                        onClick={() => handleOpenModal(media)}
                    >
                        <CardMedia
                            component="img"
                            height={isMobile ? "150" : "200"}
                            image={url}
                            alt={title}
                            sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <ImageIcon color="primary" />
                                <Chip label="Image" size="small" color="primary" variant="outlined" />
                            </Box>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {description}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                );

            default:
                return null;
        }
    };

    const renderFullScreenMedia = () => {
        if (!selectedMedia) return null;

        const { url, type, title, description } = selectedMedia;

        switch (type.toLowerCase()) {
            case 'audio':
                return (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                        <AudioFileIcon sx={{ fontSize: '4rem', color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {title}
                        </Typography>
                        {description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {description}
                            </Typography>
                        )}
                        <audio
                            controls
                            style={{ width: '100%', maxWidth: '400px' }}
                            autoPlay
                        >
                            <source src={url} type="audio/mpeg" />
                            <source src={url} type="audio/wav" />
                            Your browser does not support the audio element.
                        </audio>
                    </Box>
                );

            case 'video':
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <video
                            controls
                            autoPlay
                            style={{
                                width: '100%',
                                maxHeight: '70vh',
                                borderRadius: '8px'
                            }}
                        >
                            <source src={url} type="video/mp4" />
                            Your browser does not support the video element.
                        </video>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="body2" color="text.secondary">
                                    {description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );

            case 'image':
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={url}
                            alt={title}
                            style={{
                                width: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                        />
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="body2" color="text.secondary">
                                    {description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    if (!links || links.length === 0) {
        return null;
    }

    return (
        <>
            <Box sx={{ mt: 1, width: '100%' }}>
                {links.map((media, index) => (
                    <Box key={index}>
                        {renderMediaPreview(media)}
                    </Box>
                ))}
            </Box>

            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                TransitionComponent={Transition}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '12px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedMedia && getMediaIcon(selectedMedia.type)}
                        <Typography variant="h6">
                            {selectedMedia?.type?.toUpperCase()} Player
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseModal} color="error">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {renderFullScreenMedia()}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default StickMediaPlayer; 